import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Layout types
export const LAYOUT_TYPES = {
  GRID: 'grid',
  CAROUSEL: 'carousel',
  COVERFLOW: 'coverflow'
};

// Breakpoints
export const BREAKPOINTS = {
  MOBILE_MAX: 768,
  TABLET_MIN: 769,
  TABLET_MAX: 1024,
  DESKTOP_MIN: 1025,
  DESKTOP_MAX: 1600,
  TV_MIN: 1601
};

// LocalStorage keys
const LAYOUT_PREFERENCE_KEY = 'tapcompass_layout_preference';
const LAYOUT_PREFERENCE_EXPIRY_KEY = 'tapcompass_layout_preference_expiry';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const LayoutContext = createContext(null);

export function LayoutProvider({ children }) {
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [userPreference, setUserPreference] = useState(LAYOUT_TYPES.GRID);
  const [isLoading, setIsLoading] = useState(true);

  // Determine screen category based on width
  const screenCategory = useMemo(() => {
    if (screenWidth <= BREAKPOINTS.MOBILE_MAX) return 'mobile';
    if (screenWidth <= BREAKPOINTS.TABLET_MAX) return 'tablet';
    if (screenWidth <= BREAKPOINTS.DESKTOP_MAX) return 'desktop';
    return 'tv';
  }, [screenWidth]);

  // Check if current screen supports alternative layouts
  const supportsAlternativeLayouts = useMemo(() => {
    return screenWidth >= BREAKPOINTS.TABLET_MIN;
  }, [screenWidth]);

  // Get available layouts for current screen size
  const availableLayouts = useMemo(() => {
    if (screenWidth <= BREAKPOINTS.MOBILE_MAX) {
      // Mobile: Grid only
      return [LAYOUT_TYPES.GRID];
    }
    if (screenWidth <= BREAKPOINTS.TABLET_MAX) {
      // Tablet: Grid + Coverflow (optional)
      return [LAYOUT_TYPES.GRID, LAYOUT_TYPES.COVERFLOW];
    }
    // Desktop and above: All layouts
    return [LAYOUT_TYPES.GRID, LAYOUT_TYPES.CAROUSEL, LAYOUT_TYPES.COVERFLOW];
  }, [screenWidth]);

  // Get the effective layout based on screen size and user preference
  const getEffectiveLayout = useCallback((isTVEndpoint = false) => {
    // TV endpoints always use their own cinematic layouts - we don't control them
    if (isTVEndpoint) {
      return null; // Let TV component handle its own layout
    }

    // Mobile: Always grid
    if (screenWidth <= BREAKPOINTS.MOBILE_MAX) {
      return LAYOUT_TYPES.GRID;
    }

    // Check if user preference is valid for current screen size
    if (availableLayouts.includes(userPreference)) {
      return userPreference;
    }

    // Default to grid
    return LAYOUT_TYPES.GRID;
  }, [screenWidth, userPreference, availableLayouts]);

  // Load user preference from localStorage
  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(LAYOUT_PREFERENCE_KEY);
      const savedExpiry = localStorage.getItem(LAYOUT_PREFERENCE_EXPIRY_KEY);

      if (savedPreference && savedExpiry) {
        const expiryDate = parseInt(savedExpiry, 10);
        if (Date.now() < expiryDate) {
          setUserPreference(savedPreference);
        } else {
          // Expired, clear storage
          localStorage.removeItem(LAYOUT_PREFERENCE_KEY);
          localStorage.removeItem(LAYOUT_PREFERENCE_EXPIRY_KEY);
        }
      }
    } catch (e) {
      console.error('Error loading layout preference:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save user preference with 30-day expiry
  const setLayoutPreference = useCallback((layout) => {
    if (!Object.values(LAYOUT_TYPES).includes(layout)) {
      console.warn('Invalid layout type:', layout);
      return;
    }

    // Check if layout is available for current screen
    if (!availableLayouts.includes(layout)) {
      console.warn('Layout not available for current screen size:', layout);
      return;
    }

    setUserPreference(layout);

    try {
      const expiryDate = Date.now() + THIRTY_DAYS_MS;
      localStorage.setItem(LAYOUT_PREFERENCE_KEY, layout);
      localStorage.setItem(LAYOUT_PREFERENCE_EXPIRY_KEY, expiryDate.toString());
    } catch (e) {
      console.error('Error saving layout preference:', e);
    }
  }, [availableLayouts]);

  // Reset preference to default
  const resetLayoutPreference = useCallback(() => {
    setUserPreference(LAYOUT_TYPES.GRID);
    try {
      localStorage.removeItem(LAYOUT_PREFERENCE_KEY);
      localStorage.removeItem(LAYOUT_PREFERENCE_EXPIRY_KEY);
    } catch (e) {
      console.error('Error resetting layout preference:', e);
    }
  }, []);

  // Check if a specific layout is available
  const isLayoutAvailable = useCallback((layout) => {
    return availableLayouts.includes(layout);
  }, [availableLayouts]);

  const value = {
    // State
    screenWidth,
    screenCategory,
    userPreference,
    isLoading,
    
    // Computed
    supportsAlternativeLayouts,
    availableLayouts,
    
    // Methods
    getEffectiveLayout,
    setLayoutPreference,
    resetLayoutPreference,
    isLayoutAvailable,
    
    // Constants
    LAYOUT_TYPES,
    BREAKPOINTS
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

// Hook for components to get their layout
export function useComponentLayout(isTVEndpoint = false) {
  const { getEffectiveLayout, screenCategory, screenWidth, userPreference } = useLayout();
  
  const layout = useMemo(() => {
    return getEffectiveLayout(isTVEndpoint);
  }, [getEffectiveLayout, isTVEndpoint]);
  
  const isMobile = screenWidth <= BREAKPOINTS.MOBILE_MAX;
  const isTablet = screenWidth > BREAKPOINTS.MOBILE_MAX && screenWidth <= BREAKPOINTS.TABLET_MAX;
  const isDesktop = screenWidth > BREAKPOINTS.TABLET_MAX && screenWidth <= BREAKPOINTS.DESKTOP_MAX;
  const isTV = screenWidth > BREAKPOINTS.DESKTOP_MAX;
  
  return {
    layout,
    screenCategory,
    isMobile,
    isTablet,
    isDesktop,
    isTV,
    userPreference
  };
}

export default LayoutContext;
